import type { AutocompleteOption } from "@/components/ui/autocomplete";

export type FocusTaxCodeType = "ncm" | "cfop";
export type FocusTaxCodeMode = "intra" | "inter" | "exact";
export type FocusTaxCodeSearchParam = "codigo" | "descricao";

export interface FocusTaxCodeOption extends AutocompleteOption {
  description: string;
  raw?: unknown;
}

export interface FocusNcmResponse {
  codigo?: string | number | null;
  descricao_completa?: string | null;
}

export interface FocusCfopResponse {
  codigo?: string | number | null;
  descricao?: string | null;
}

export interface FocusTaxCodeSearchInput {
  type: FocusTaxCodeType;
  query: string;
  mode?: FocusTaxCodeMode;
  offset?: number;
}

export interface FocusTaxCodeSearchPayload extends FocusTaxCodeSearchInput {
  query: string;
  searchParam: FocusTaxCodeSearchParam;
}

const digitsOnly = (value: string) => value.replace(/\D/g, "");

const normalizeDescription = (description: string, code: string) =>
  description.replace(new RegExp(`^${code}\\s*-\\s*`), "").trim();

export function normalizeFocusNcm(row: FocusNcmResponse): FocusTaxCodeOption {
  const value = String(row.codigo ?? "").trim();
  const description = String(row.descricao_completa ?? "").trim();
  return {
    value,
    label: description ? `${value} - ${description}` : value,
    description,
    raw: row,
  };
}

export function normalizeFocusCfop(row: FocusCfopResponse): FocusTaxCodeOption {
  const value = String(row.codigo ?? "").trim();
  const description = normalizeDescription(String(row.descricao ?? "").trim(), value);
  return {
    value,
    label: description ? `${value} - ${description}` : value,
    description,
    raw: row,
  };
}

export function buildFocusTaxCodeSearchPayload(input: FocusTaxCodeSearchInput): FocusTaxCodeSearchPayload {
  const trimmedQuery = input.query.trim();
  const numericQuery = digitsOnly(trimmedQuery);

  if (input.type === "cfop") {
    const fallbackQuery = input.mode === "inter" ? "6" : input.mode === "intra" ? "5" : "";
    const modePrefix = input.mode === "inter" ? "6" : input.mode === "intra" ? "5" : "";
    const isNumericSearch = numericQuery.length === trimmedQuery.length && numericQuery.length > 0;
    const prefixedQuery = modePrefix && isNumericSearch && !numericQuery.startsWith(modePrefix)
      ? modePrefix
      : numericQuery;
    return {
      ...input,
      query: isNumericSearch ? prefixedQuery || fallbackQuery : trimmedQuery || fallbackQuery,
      searchParam: isNumericSearch || !trimmedQuery ? "codigo" : "descricao",
    };
  }

  return {
    ...input,
    query: trimmedQuery,
    searchParam: numericQuery.length === trimmedQuery.length && numericQuery.length > 0 ? "codigo" : "descricao",
  };
}
